/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';

import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import { IJobOverview } from '../../../../Jobs/Components/JobsListView/JobsListView';
import { IJobs } from '../../../../Jobs/Models/JobsView.Model';
import { formatTimeTo12Hr } from '../../../../../Shared/Utilities/utils';
import { IBidList } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../../Customer/Components/ViewFacilityDetails/ViewFacilityDetails';
import { TaskData } from '../../../../Jobs/Components/WorkTasks/WorkTasks';
import { CurrencySymbol, Paths, Types } from '../../../../../Shared/Constants/App';
import DataGrid from '../../../../../Shared/Components/Common/DataGrid/DataGrid';
import ModifySchedulePopup from './ModifySchedulePopup';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';

import { Icons } from '../../../../../Shared/Constants/Icons';
import theme from '../../../../../Shared/Themes/theme';

import WhiteTooltip from '../../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';
import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { ActionsColumn, Linking } from '../../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import { ModifyButtonContainer } from '../AllJobs.Style';
import { getReadableScheduleDays } from '../AllSchedulesColumns';
import { IOpenPosition } from '../AllSchedules.Model';

const ModifySchedule = (): JSX.Element => {
  const location = useLocation();
  const jobsData = location?.state?.selectedRows;
  const [tableData, setTableData] = useState<any[]>([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation(['jobManagement']);
  const navigate = useNavigate();

  const renderCell = useCallback((renderedCellValue: React.ReactNode): string | number => {
    if (renderedCellValue === 0) {
      return 0;
    }
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return renderedCellValue ? renderedCellValue : '-';
    }
    if (renderedCellValue && Array.isArray(renderedCellValue)) {
      return renderedCellValue[0]?.finalQuote || renderedCellValue[0]?.finalQuote === 0
        ? `${CurrencySymbol.Dollar}${Number(renderedCellValue[0]?.finalQuote).toFixed(2)}`
        : '-';
    }
    return '-';
  }, []);

  const handleOpenPopup = (rowData: any) => {
    setOpenPopup(true);
    setSelectedRows([rowData]);
  };

  const handleCreateSchedule = (rowData: any) => {
    navigate(Paths.CreateJobSchedule, {
      state: {
        selectedRows: rowData,
      },
    });
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
  };

  const renderOpenPositions = (renderedCellValue: React.ReactNode | IOpenPosition): string => {
    if (typeof renderedCellValue !== Types.Object || renderedCellValue === null) {
      return '-';
    }

    if (renderedCellValue && typeof renderedCellValue === Types.Object) {
      const { isPositionOpen, totalPositionsOpen } = renderedCellValue as IOpenPosition;
      return isPositionOpen ? `${t('jobManagement:yes')} (${totalPositionsOpen ?? 0})` : t('jobManagement:no');
    }
    return '-';
  };

  const handleConfirmModification = async (data: {
    scheduleName: string;
    woScheduleMasterId: string;
    scheduleDays: string;
    startTime: string;
    endTime: string;
    shiftType: string;
    endDateOffset: number;
    totalFTE: number;
    scheduleStartDate: Date | null;
    scheduleEndDate?: Date | null;
  }) => {
    try {
      const updateScheduleInput = {
        woScheduleMasterId: data.woScheduleMasterId,
        scheduleDays: data.scheduleDays,
        scheduleStartTime: data.startTime?.slice(0, 5),
        scheduleEndTime: data.endTime?.slice(0, 5),
        mdShiftType: data?.shiftType,
        scheduleName: data?.scheduleName,
        endDateOffset: Number(data?.endDateOffset) || 0,
        totalFTE: data?.totalFTE,
        scheduleStartDate: data?.scheduleStartDate,
        scheduleEndDate: data?.scheduleEndDate,
      };
      const response = await AllJobsApiService.updateScheduleMaster([updateScheduleInput]);
      if (response?.data && !response?.errors?.[0]?.message) {
        setSnackbarMessage(t('jobManagement:updatedTheSchedule'));
        setSnackbarOpen(true);
        setSnackbarSeverity('success');
        setTimeout(() => {
          setOpenPopup(false);
          navigate('/all-schedules');
        }, 1000);
        setIsDeletePopupVisible(false);
        setIsLoading(false);
      } else {
        setSnackbarMessage(response?.errors?.[0]?.message);
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        setTimeout(() => {
          setOpenPopup(false);
        }, 1000);
        setIsDeletePopupVisible(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(t('jobManagement:errorUpdatingSchedule'), error);
      setIsDeletePopupVisible(false);
      setIsLoading(false);
    }
  };

  const renderActionsCell = (row: any): JSX.Element => {
    return (
      <ActionsColumn>
        <Linking to="#" onClick={() => handleOpenPopup(tableData[row.index])}>
          <IconButton
            size="large"
            sx={{
              '&:hover': {
                background: theme.palette.primary.main,
              },
            }}>
            <WhiteTooltip title={t('jobManagement:modifyJobSchedule')}>
              <img src={Icons.EditBlackIcon} />
            </WhiteTooltip>
          </IconButton>
        </Linking>
      </ActionsColumn>
    );
  };

  useEffect(() => {
    if (jobsData) {
      setTableData(jobsData);
    }
  }, []);

  const columns = useMemo<MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[]>(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    () => [
      {
        accessorKey: 'jobNumber',
        header: 'Job Number',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobName',
        header: 'Job Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobAddress',
        header: 'Job Address',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'scheduleName',
        header: 'Schedule Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'scheduleStartTime',
        header: 'Schedule Start Time',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) => {
          const time = String(renderedCellValue);
          return time ? formatTimeTo12Hr(time) : '';
        },
      },
      {
        accessorKey: 'scheduleEndTime',
        header: 'Schedule End Time',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) => {
          const time = String(renderedCellValue);
          return time ? formatTimeTo12Hr(time) : '';
        },
      },
      {
        accessorKey: 'mdScheduleType',
        header: 'Schedule Type',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'mdShiftType',
        header: 'Shift Type',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'scheduleDays',
        header: 'Schedule Days',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const scheduleDays = cell.getValue() as string[];
          const scheduleType = cell.row.original.mdScheduleType;
          return getReadableScheduleDays(scheduleType, scheduleDays);
        },
      },
      {
        accessorKey: 'punchInGraceMinutes',
        header: 'Punch In Grace Minutes',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => `${renderCell(renderedCellValue)} minutes`,
      },
      {
        accessorKey: 'punchOutGraceMinutes',
        header: 'Punch Out Grace Minutes',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => `${renderCell(renderedCellValue)} minutes`,
      },
      {
        accessorKey: 'endDateOffset',
        header: 'End Date Offset',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => {
          const value = renderCell(renderedCellValue);
          return `${value} ${value === 1 || value === 0 ? 'day' : 'days'}`;
        },
      },
      {
        accessorKey: 'totalFTE',
        header: 'Number of Full Time Employees',
        enableSorting: false,
        enableColumnFilter: false,
        size: 180,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'noOfAssociatesAssigned',
        header: 'Number of Employees Assigned',
        enableSorting: false,
        enableColumnFilter: false,
        size: 180,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'openPositions',
        header: 'Open Positions',
        enableSorting: false,
        enableColumnFilter: false,
        size: 100,
        Cell: ({ renderedCellValue }) => {
          return renderOpenPositions(renderedCellValue);
        },
      },
      {
        header: 'Actions',
        enableColumnActions: false,
        enablePinning: false,
        size: 90,
        Cell: ({ row }) => renderActionsCell(row),
      },
    ],
    []
  );

  return (
    <>
      <PageTitle title={t('jobManagement:modifyJobSchedule')} />
      <CustomerContainer>
        <DataGrid
          columns={columns}
          data={tableData || []}
          enableRowSelect={false}
          enableColumnPinning={false}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          configureColumns={true}
        />
        <ModifyButtonContainer sx={{ marginTop: '2rem', gap: '2rem' }}>
          <Button
            type={ButtonType.Primary}
            label={t('jobManagement:createJobSchedule')}
            onClick={() => handleCreateSchedule(tableData)}
          />
          <Button
            type={ButtonType.Primary}
            label={t('jobManagement:modifyJobSchedule')}
            onClick={() => handleOpenPopup(tableData)}
          />
        </ModifyButtonContainer>
        <ModifySchedulePopup
          selectedJobData={tableData}
          open={openPopup}
          onClose={handleClosePopup}
          onConfirm={handleConfirmModification}
          setOpenPopup={setOpenPopup}
          selectedRows={selectedRows}
          isDeletePopupVisible={isDeletePopupVisible}
          setIsDeletePopupVisible={setIsDeletePopupVisible}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </CustomerContainer>
    </>
  );
};

export default ModifySchedule;
